<template>
  <section>
    <one-line-form :submit="updateCity" :submittable="isValid" :button="showCities === null || cities.length <= 1" :saving="isSaving ? city : ''">
      <input-field name="city"
                   :class="{ invisible: isSaving || !openAnswer }"
                   type="text"
                   :value="city"
                   @input="syncCity"
                   @focus="selectCity"
                   :pattern="pattern"
                   :title="i18n.t('city:city')"
                   :placeholder="i18n.t('city:enter_city')"
                   :autocomplete="'address-level2'"
                   :autocapitalize="true" />
    </one-line-form>
    <div v-if="!openAnswer && visibleQuestion && !isSaving" :button="false" class="question">
      <h5>{{ i18n.t('city:select_city') }}</h5>
    </div>
    <helper v-if="entity.country === 'US'">
      {{ entity.province }}{{ entity.postalCode === user.GeoPostalCode || entity.postalCode === app.unknownPostcode ? '' : `, ${entity.postalCode}`}}
    </helper>
    <helper v-else :class="{ invisible : isSaving }">
      {{ entity.postalCode === user.GeoPostalCode || entity.postalCode === app.unknownPostcode ? '' : entity.postalCode}}
    </helper>
    <numb-area></numb-area>
    <option-selector v-if="showCities" :options="cities" @submit="selectCity" :has-other="true" @other="selectCity()"></option-selector>
    
    <show-keyboard v-if="!cities || showCities === null"></show-keyboard>
  </section>
</template>

<style lang="scss" rel="stylesheet/scss" scoped>
  @import '../assets/mixins';

  ul {
    @include button-list;
  }
  .actionhint {
    pointer-events: none;
    bottom: -40px;
  }
  form .invisible{
    position: absolute;
  }
</style>

<script>
  import Vue from 'vue'
  import { mapState } from 'vuex'

  import { setSelection } from '../utils/selection'
  import { getNestedProperty, areConditionsPassed } from '../utils/structure.js'
  import fieldCustomRules from '../mixins/customFieldsRules'

  export default Vue.component('personal-city-screen', {
    props: ['screenParams'],
    mixins : [
      fieldCustomRules
    ],
    data () {
      const autofill = this.$store.state.autofill
      const postalCodePrediction = this.$store.state.postalCodePrediction
      const companyPrediction = this.$store.state.companyPrediction && this.$store.state.companyPrediction.matchCompany

      const dispatchKeyConditions = this.screenParams && this.screenParams.dispatchKey && this.screenParams.dispatchKey.conditions
      const dispatchKeyString = this.screenParams && this.screenParams.dispatchKey && typeof this.screenParams.dispatchKey === 'string' && this.screenParams.dispatchKey
      const dispatchKey = (dispatchKeyConditions && areConditionsPassed(this.$store, this.screenParams.dispatchKey.conditions) && this.screenParams.dispatchKey.prop) || dispatchKeyString || 'updateUser'
      const key = dispatchKey.replace('update', '').toLowerCase()
      const propertyName = (this.screenParams && this.screenParams.propertyName) || 'city'

      let propertyEntity = propertyName.split('.')
      propertyEntity = propertyEntity.length > 1 ? propertyEntity.slice(0, -1).join('.') : false
      const entityData = propertyEntity ? getNestedProperty(this.$store.state[key], propertyEntity) : this.$store.state[key]

      const userCityPrediction = postalCodePrediction.enabled && postalCodePrediction.matchPerson.city
      const companyCityPrediction = companyPrediction && companyPrediction.city

      return {
        city: entityData.city || (key === 'user' && userCityPrediction) || (key === 'company' && companyCityPrediction) || (autofill.enabled && autofill.city) || '',
        postalCode: propertyEntity.postalCode,
        showCities: false,
        isSaving: false,
        visibleQuestion: true,
        entity: entityData,
        propertyName: propertyName,
        dispatchKey: (this.screenParams && this.screenParams.dispatchKey) || 'updateUser'
      }
    },
    computed: {
      ...mapState(['user', 'postalArea', 'app', 'autofill']),
      pattern () {
        const customFieldRuless = this.getFieldCustomRules("city")
        const maxLength = customFieldRuless && customFieldRuless.maxLength
        return maxLength ? '_'.repeat(maxLength) : null
      },
      cities () {
        return (this.postalArea.cities || [])
      },
      openAnswer () {
        return this.showCities === null || this.cities.length <= 1
      },
      isValid () {
        return this.isValidLength(this.city,'city')
      }
    },
    watch: {
      postalArea () {
        if (this.postalArea.cities && this.postalArea.cities.length > 1) {
          this.showCities = true
        }
      },
      isValid () {
        if (this.isValid && this.showCities) {
          this.$emit('tooltip', ['vertical', 'enter'])
        } else if (this.isValid) {
          this.$emit('tooltip', ['enter'])
        } else if (!this.showCities) {
          this.$emit('tooltip', ['type'])
        }
      },
      isSaving () {
        if (this.isSaving) {
          this.$emit('tooltip', 'hide')
        }
      }
    },
    mounted () {
      if (this.city === '' && this.postalArea.cities && this.postalArea.cities.length > 1 && this.entity.postalCode !== this.app.unknownPostcode) {
        setTimeout(() => {
          this.showCities = true
        }, 300)
        this.$emit('tooltip', ['vertical', 'enter'])
      } else {
        this.selectCity()
        this.$emit('tooltip', ['type'])
      }
    },
    methods: {
      selectCity (selectedCity) {
        this.visibleQuestion = false

        if (selectedCity && selectedCity.value) {
          this.$store.dispatch('updateCity', selectedCity)
          this.city = selectedCity.value
          this.updateCity(selectedCity)
        } else if (!selectedCity && this.showCities !== null) {
          this.showCities = null
        }

        this.$emit('tooltip', ['type'])

        const input = this.$el.querySelector('input')

        if (input) {
          if (this.isSaving) input.setAttribute('readonly', 'true')
          setSelection(input, { start: 0, end: 0 })
        }
      },
      syncCity (value) {
        if (this.usingPrefill && value !== this.city) this.usingPrefill = false
        this.city = value
      },
      updateCity (selectedCity) {
        if (this.city) {
          this.isSaving = true

          let dispatchData = {}

          if (this.app.country.code === 'DE') {
            const property = this.propertyName.split('.')
            const propertyProvince = property.length > 1 ? property.slice(0, -1).join('.') + '.province' : 'province'
  
            const provinceName = selectedCity && selectedCity.overrides && selectedCity.overrides.state
            if (provinceName) {
              const provinceMatch = this.app.country.provinces.find((province) => {
                return province.name === provinceName
              })
              dispatchData[propertyProvince] = provinceMatch.ISOCode ? provinceMatch.ISOCode.slice(-2) : ''
            }
          }

          dispatchData[this.propertyName] = this.city
          this.$store.dispatch(this.dispatchKey, dispatchData)

          this.trackPrefill('city', this.city)

          setTimeout(() => {
            this.showCities = null
            this.$store.dispatch('next', this.screenParams.nextScreenRoute)
          }, 300)
        }
      }
    }
  })
</script>



// WEBPACK FOOTER //
// src/screens/PersonalCity.vue